import { db, withSyncWrite } from '../db/indexeddb'
import type { SyncConflict } from './types'
import type { SyncQueueItem } from '../db/models'
import type Dexie from 'dexie'

/**
 * Generic Pull Engine
 * Pulls remote entities from cloud into local DB using a descriptor-based approach.
 */

/** Configuration for pulling a specific entity type from cloud. */
export interface PullDescriptor<TRemote> {
  entityName: string
  settingsKey: string

  // Remote operations
  fetchRemote: (userId: string, since?: Date) => Promise<TRemote[]>
  fetchRemoteIds: (userId: string) => Promise<string[]>
  fetchRemoteByIds: (userId: string, ids: string[]) => Promise<TRemote[]>
  fetchDeletedRemote: (userId: string, since?: Date) => Promise<Array<{ id: string; local_id?: string | null; updated_at: string }>>

  // Schema & mapping
  safeParse: (raw: unknown) => { success: true; data: TRemote } | { success: false; error: { message: string } }
  getRemoteId: (remote: TRemote) => string
  getRemoteTimestamp: (remote: TRemote) => string
  getRemoteLocalId: (remote: TRemote) => string | null | undefined

  // Local DB operations
  table: Dexie.Table
  getAllLocal: () => Promise<Array<{ id: string; cloudId: string | null; synced: boolean; updatedAt: Date; deletedAt: Date | null; [key: string]: any }>>

  // Entity-specific create/update logic
  buildLocalInsert: (remote: TRemote, userId: string) => Promise<Record<string, any> | null>
  buildLocalUpdate: (remote: TRemote, existing: any) => Promise<Record<string, any> | null>

  // Optional: safety check to force full sync (e.g. zero local tasks)
  shouldForceFullSync?: (sinceDate: Date) => Promise<boolean>
}

export interface PullResult {
  created: number
  updated: number
  deleted: number
  conflicts: SyncConflict[]
  errors: string[]
}

/**
 * Generic pull: fetches remote entities and applies them locally.
 * Implements the 7-step algorithm shared by tasks, projects, and sections.
 */
export async function pullRemoteEntities<TRemote>(
  userId: string,
  descriptor: PullDescriptor<TRemote>,
  pendingChanges: SyncQueueItem[],
  cloudCount: number | null,
): Promise<PullResult> {
  const result: PullResult = { created: 0, updated: 0, deleted: 0, conflicts: [], errors: [] }

  try {
    // 1. Read last sync timestamp
    const lastSyncValue = await db.getSettingValue<string>(descriptor.settingsKey)
    let sinceDate = lastSyncValue ? new Date(lastSyncValue) : undefined

    // Optional safety check (e.g. zero local tasks → force full sync)
    if (sinceDate && descriptor.shouldForceFullSync) {
      if (await descriptor.shouldForceFullSync(sinceDate)) {
        console.log(`[Sync] No local ${descriptor.entityName}s found — forcing full sync`)
        sinceDate = undefined
      }
    }

    // 2. Fetch remote records
    const remoteRecords = await descriptor.fetchRemote(userId, sinceDate)

    // 3. Build local lookup maps
    const allLocal = await descriptor.getAllLocal()
    const localById = new Map<string, typeof allLocal[0]>()
    const localByCloudId = new Map<string, typeof allLocal[0]>()
    for (const entity of allLocal) {
      localById.set(entity.id, entity)
      if (entity.cloudId) localByCloudId.set(entity.cloudId, entity)
    }

    // 4. Repair stale synced=false flags (entities corrupted by old code)
    const pendingEntityIds = new Set(
      pendingChanges.filter(c => c.table === descriptor.entityName + 's').map(c => c.data?.localId)
    )
    let repaired = 0
    for (const entity of allLocal) {
      if (entity.synced === false && entity.cloudId && !pendingEntityIds.has(entity.id)) {
        await withSyncWrite(() => descriptor.table.update(entity.id, { synced: true }))
        entity.synced = true
        repaired++
      }
    }
    if (repaired > 0) {
      console.log(`[Sync] Repaired ${repaired} ${descriptor.entityName}s with stale synced=false`)
    }

    console.log(`[Sync] pull${descriptor.entityName}s: ${remoteRecords.length} fetched (since: ${sinceDate?.toISOString() ?? 'full'})`)

    // 5. ID-set reconciliation (count-gated)
    if (sinceDate) {
      const localCloudIdCount = localByCloudId.size
      const needsReconciliation = cloudCount === null || cloudCount !== localCloudIdCount

      if (needsReconciliation) {
        console.log(`[Sync] ${descriptor.entityName} reconciliation triggered (cloud: ${cloudCount ?? '?'}, local: ${localCloudIdCount}) — fetching IDs`)
        const cloudIds = await descriptor.fetchRemoteIds(userId)
        const alreadyFetchedIds = new Set(remoteRecords.map(r => descriptor.getRemoteId(r)))
        const missingIds = cloudIds.filter(id => !localByCloudId.has(id) && !alreadyFetchedIds.has(id))

        if (missingIds.length > 0) {
          console.log(`[Sync] ID-set reconciliation: ${missingIds.length} ${descriptor.entityName}s in cloud but not local — fetching`)
          const missing = await descriptor.fetchRemoteByIds(userId, missingIds)
          remoteRecords.push(...missing)
        }
      }
    }

    // 6. Process each remote record: validate → match → create or update
    for (const rawRemote of remoteRecords) {
      const parseResult = descriptor.safeParse(rawRemote)
      if (!parseResult.success) {
        const id = (rawRemote as any).id ?? 'unknown'
        result.errors.push(`Invalid remote ${descriptor.entityName} ${id}: ${parseResult.error.message}`)
        continue
      }
      const remote = parseResult.data

      const remoteId = descriptor.getRemoteId(remote)
      const remoteLocalId = descriptor.getRemoteLocalId(remote)

      // Match by local_id first (stable identity), then fall back to cloudId (legacy)
      const localEntity = (remoteLocalId ? localById.get(remoteLocalId) : null)
        || localByCloudId.get(remoteId)

      if (!localEntity) {
        // Insert new record
        try {
          // Guard against duplicates from concurrent sync runs: re-check Dexie
          // for an entity with this cloudId before inserting.
          const existingByCloudId = await descriptor.table.where('cloudId').equals(remoteId).first()
          if (existingByCloudId) continue

          const insertData = await descriptor.buildLocalInsert(remote, userId)
          if (!insertData) continue // skipped intentionally (e.g. parent not yet synced)
          await withSyncWrite(() => descriptor.table.add(insertData))
          result.created++
        } catch (error: any) {
          const msg = error?.name === 'SchemaError'
            ? `[SCHEMA] ${descriptor.entityName}: ${error.message} — DB upgrade needed`
            : `Failed to create local ${descriptor.entityName}: ${error}`
          console.error(`[Sync] ${msg}`)
          result.errors.push(msg)
        }
      } else {
        // Update existing record
        const updates: Record<string, any> = {}

        // Backfill cloudId if missing (legacy)
        if (!localEntity.cloudId) {
          updates.cloudId = remoteId
          updates.userId = userId
        }

        // Build entity-specific updates (LWW: remote wins when local is clean and older)
        const remoteUpdated = new Date(descriptor.getRemoteTimestamp(remote))
        let appliedRemoteUpdate = false
        if (localEntity.synced !== false && remoteUpdated > localEntity.updatedAt) {
          const entityUpdates = await descriptor.buildLocalUpdate(remote, localEntity)
          if (entityUpdates) {
            Object.assign(updates, entityUpdates)
            appliedRemoteUpdate = true
          }
        } else if (localEntity.synced === false) {
          console.log(`[Sync] PULL skipped (dirty): ${descriptor.entityName} cloudId=${remoteId}`)
        }

        if (Object.keys(updates).length > 0) {
          await withSyncWrite(() =>
            descriptor.table.update(localEntity.id, { ...updates, synced: true, syncedAt: new Date() })
          )
          if (appliedRemoteUpdate) result.updated++
        }
      }
    }

    // 7. Pull remote deletions
    const deletedRemote = await descriptor.fetchDeletedRemote(userId, sinceDate)
    for (const deleted of deletedRemote) {
      const localEntity = ((deleted as any).local_id ? localById.get((deleted as any).local_id) : null)
        || localByCloudId.get(deleted.id)
      if (localEntity && !localEntity.deletedAt && localEntity.synced !== false) {
        await withSyncWrite(() => descriptor.table.update(localEntity.id, { deletedAt: new Date() }))
        result.deleted++
      }
    }

    // Advance sync timestamp using MAX server time
    if (result.errors.length === 0) {
      let maxServerTime = sinceDate?.toISOString() ?? ''
      for (const r of remoteRecords) {
        const ts = descriptor.getRemoteTimestamp(r)
        if (ts > maxServerTime) maxServerTime = ts
      }
      for (const d of deletedRemote) {
        if (d.updated_at > maxServerTime) maxServerTime = d.updated_at
      }
      const newSyncTime = maxServerTime || new Date().toISOString()
      await db.setSetting(descriptor.settingsKey, newSyncTime)
    }

    return result
  } catch (error: any) {
    const msg = error?.name === 'SchemaError'
      ? `[SCHEMA] Pull ${descriptor.entityName}s failed: ${error.message} — DB upgrade needed`
      : `Pull ${descriptor.entityName}s failed: ${error}`
    console.error(`[Sync] ${msg}`)
    result.errors.push(msg)
    return result
  }
}
